import { all, put, call, select, takeEvery, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import config from '@/services/Api/apiConfig';
import ApiService from '@/services/Api';
import actions, { actionTypes } from './actions';
import modalsActions from '../modals/actions';
import leadActions from '../lead/actions';
import { mapValuesToConstants } from '@/utils/redux';
import { zoom_level } from '../../components/Maps/RegularMap/RegularMap.js';
import { message } from 'antd';

function* submitForm({ payload }) {
  const { utilityBills, additionalFiles, stepsData } = yield select(({ intakeForm }) => ({
    utilityBills: intakeForm.utilityBills,
    additionalFiles: intakeForm.additionalFiles,
    stepsData: intakeForm.stepsData,
  }));

  const {
    projectName,
    customerFirstName,
    customerLastName,
    customerPhoneNumber,
    customerPhoneExtension,
    customerEmail,
    propertyLocation,
    utilityName,
    calculatedSystemSize,
    systemProduction,
    proposalType,
    upgrades,
    workingWithRoofer,
    roofWarranty,
    warrantyLength,
    customerType,
    buildingType,
    projectNotes,
    systemSize,
    estimatedYearOneProd,
    epcPrice,
    avoidedCost,
    installTypeProperty,
  } = stepsData.summary;

  const requestData = {
    customer: {
      first_name: customerFirstName,
      last_name: customerLastName,
      phone_numbers: customerPhoneNumber?.split(),
      phone_extension: customerPhoneExtension,
      emails: customerEmail?.split(),
    },
    property: {
      site_address: propertyLocation,
      building_use: mapValuesToConstants(buildingType),
      utility_name: utilityName,
      calculated_system_size: calculatedSystemSize,
      calculated_system_production: systemProduction,
      project_notes: projectNotes,
    },
    project_info: {
      system_size: systemSize,
      estimated_year_production: estimatedYearOneProd,
      epc_price: epcPrice,
      avoided_cost: avoidedCost,
      install_type: mapValuesToConstants(installTypeProperty),
    },
    zoom_level: zoom_level,
    name: projectName,
    proposal_type: mapValuesToConstants(proposalType),
    upgrades: upgrades,
    working_with_roofer: mapValuesToConstants(workingWithRoofer),
    roof_warranty: mapValuesToConstants(roofWarranty),
    warranty_length: warrantyLength,
    owner_type: mapValuesToConstants(customerType),
    utility_bills: utilityBills,
    additional_files: additionalFiles,
  };

  try {
    if (payload.from === 'step2') {
      const { data } = yield call(ApiService.createLead, requestData);
      yield put(actions.step2SubmitSuccess(data));
      const leadEnrichmentStatusMessage = message.loading('Please wait while we pre-qualify this address.', 0);
      const { enriched_data } = yield call(ApiService.fetchLeadEnrichmentStatus, data.id);
      leadEnrichmentStatusMessage();
      message.success('Lead submitted successfully.');
      yield put(leadActions.redirectToLead(data.id));
    }
  } catch (error) {
    yield put(actions.submitError(error));
  }
}

function handleStartFileUpload(file, token, url, fileType, leadId) {
  const formData = new FormData();
  formData.append('file', file);
  if (fileType !== undefined) {
    formData.append('file_type', fileType);
  }
  if (leadId !== undefined) {
    formData.append('lead_pk', leadId);
  }

  return axios({
    method: 'put',
    url: `${config.BASE_URI}/${url}`,
    data: formData,
    headers: {
      'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
      Authorization: `Token ${token}`,
    },
  });
}

function* fileUpload({ payload }) {
  const { file, fileType, onSuccess, leadId } = payload;
  let url;

  if (payload.step === 'step4') {
    url = '/api/utility_bill_upload/';
  } else {
    url = '/api/additional_file_upload/';
  }

  try {
    const { token } = yield select(({ auth }) => ({
      token: auth.profile.token,
    }));

    const { data } = yield call(handleStartFileUpload, file, token, url, fileType, leadId);
    yield call(onSuccess, data, file);
    yield put(
      actions.fileUploadSuccess({
        data,
        file: file,
      })
    );
  } catch (error) {
    yield put(actions.fileUploadError(file));
  }
}

function* fetchUapiData(token) {
  try {
    const response = yield call(ApiService.fetchUapiData, token);
    yield put(actions.fetchUapiSuccess(response.data));
    yield put(modalsActions.toggleUapiIframeModal(false));
    const { uApiData } = yield select(({ intakeForm }) => ({
      uApiData: intakeForm.uApiData,
    }));
    const list = uApiData.map((list) =>
      list.exports_list.map((file) => ({
        link: file.link,
        name: file.name,
      }))
    );
    const linksList = list.flat();
    yield call(fetchUapiFiles, {
      linksList,
    });
  } catch (error) {
    yield put(actions.fetchUapiFailed(error));
  } finally {
    yield put(modalsActions.toggleUapiIframeModal(false));
  }
}

function* fetchUapiFiles({ linksList }) {
  try {
    const { data } = yield call(ApiService.fetchUapiFiles, linksList);
    yield put(actions.fetchUapiFilesSuccess(data));
  } catch (error) {
    yield put(actions.fetchUapiFilesFailed(error));
  }
}

function* utilityApiAuth({ payload }) {
  const referralUrl = payload.substring(1).split('=', 2);
  yield put(actions.saveUapiReferralId(referralUrl[1]));
  yield call(fetchUapiData, referralUrl[1]);
}

export default function* () {
  yield all([
    takeEvery(actionTypes.FILE_UPLOAD, fileUpload),
    takeLatest(actionTypes.SUBMIT_FORM, submitForm),
    takeLatest(actionTypes.FETCH_UAPI_DATA, fetchUapiData),
    takeLatest(actionTypes.FETCH_UAPI_FILES, fetchUapiFiles),
    takeEvery(actionTypes.UTILITY_API_AUTH, utilityApiAuth),
  ]);
}
